import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Brain, HardHat, Globe, Thermometer, Activity, Bot, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { isEnglishPath } from '../../utils/i18nRoutes';

const ICONS = [Brain, HardHat, Globe, Thermometer, Activity, Bot];
const COLORS = ['#8DC21F', '#5A8C1A', '#8DC21F', '#f97316', '#a78bfa', '#8DC21F'];
// All 6 technology cards now have a dedicated page (both ES and EN versions).
const CARD_LINKS_ES = ['/pando', '/halo-i', '/digital-twin', '/canaria', '/exoesqueleto', '/robotica'];
const CARD_LINKS_EN = ['/en/pando', '/en/halo-i', '/en/digital-twin', '/en/canaria', '/en/exoesqueleto', '/en/robotica'];

// Service card videos
// Pando AI & HALO-I Helmet: existing Asme CDN videos
// Digital Twin: Madrid digital twin demo video
// Canaria+: Biodata Bank Canaria+ wearable video
// Exoskeleton: Hypershell ergonomic assistance video
// Robotics: Unitree G1 humanoid & Go2 Pro quadruped demo
const CARD_VIDEOS = [
  'https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260314_131748_f2ca2a28-fed7-44c8-b9a9-bd9acdd5ec31.mp4',
  'https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260324_151826_c7218672-6e92-402c-9e45-f1e0f454bdc4.mp4',
  'https://www.youtube.com/embed/c50Z-S-UnPI?autoplay=1&mute=1&loop=1&playlist=c50Z-S-UnPI&controls=0&showinfo=0&modestbranding=1', // Digital Twin Madrid
  'https://www.youtube.com/embed/fQvwB98VwJ0?autoplay=1&mute=1&loop=1&playlist=fQvwB98VwJ0&controls=0&showinfo=0&modestbranding=1', // Canaria+ Heat Warning Watch
  'https://www.youtube.com/embed/RceSmAL3nN8?autoplay=1&mute=1&loop=1&playlist=RceSmAL3nN8&controls=0&showinfo=0&modestbranding=1', // Hypershell X Pro (exoskeleton demo)
  'https://www.youtube.com/embed/AyWFSSbxdh0?autoplay=1&mute=1&loop=1&playlist=AyWFSSbxdh0&controls=0&showinfo=0&modestbranding=1', // Unitree G1 & Go2 features
];

function ProjectCard({ item, index, inView, cardLinks }) {
  const Icon = ICONS[index];
  const color = COLORS[index];
  const videoSrc = CARD_VIDEOS[index];
  const isYouTube = videoSrc && videoSrc.includes('youtube.com');
  const linkTo = cardLinks[index];
  const navigate = useNavigate();

  return (
    <motion.div
      data-testid="innovation-project-card"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="group liquid-glass rounded-3xl overflow-hidden flex flex-col"
      style={{ border: 'none', cursor: linkTo ? 'pointer' : 'default' }}
      whileHover={{ y: -3 }}
      onClick={linkTo ? () => navigate(linkTo) : undefined}
      role={linkTo ? 'link' : undefined}
      tabIndex={linkTo ? 0 : undefined}
    >
      {/* Video or color header */}
      {videoSrc ? (
        <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
          {isYouTube ? (
            <iframe
              src={videoSrc}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              title={item.name}
            />
          ) : (
            <video
              muted autoPlay loop playsInline preload="auto"
              className="w-full h-full object-cover"
              style={{ transition: 'transform 0.7s', transform: 'scale(1)' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0" style={{ background: isYouTube ? 'none' : 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)', pointerEvents: 'none' }} />
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span
              className="text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-full"
              style={{ background: 'rgba(0,0,0,0.6)', color: color, backdropFilter: 'blur(4px)' }}
            >
              {item.tags[0]}
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <div className="liquid-glass rounded-full p-2" style={{ border: 'none' }}>
              <ArrowUpRight size={14} className="text-white/60 dark:text-white/60 group-hover:text-white" />
            </div>
          </div>
        </div>
      ) : (
        <div
          className="relative flex items-center justify-center"
          style={{ height: '80px', background: `${color}10`, borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: `${color}20`, border: `1px solid ${color}30` }}
          >
            <Icon size={22} style={{ color }} />
          </div>
          <div className="absolute top-3 right-3 flex flex-wrap gap-1 justify-end">
            {item.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-[9px] font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}>{tag}</span>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-base font-bold text-black dark:text-white/90 tracking-tight mb-0.5">{item.name}</h3>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] mb-3" style={{ color }}>{item.subtitle}</p>
        <p className="text-sm text-gray-700 dark:text-white/55 leading-relaxed flex-1 mb-4">{item.desc}</p>
        <div style={{ paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.07)' }} className="dark:border-white/10">
          <span className="text-xs font-semibold" style={{ color }}>{item.metric}</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function ProjectsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const cardLinks = isEnglishPath(pathname) ? CARD_LINKS_EN : CARD_LINKS_ES;
  const items = t('projects.items');

  return (
    <section
      id="proyectos"
      data-testid="innovation-ecosystem-section"
      ref={ref}
      className="overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #000 0%, rgba(3,8,3,0.98) 50%, #000 100%)',
        paddingTop: '6rem',
        paddingBottom: '7rem',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
      }}
    >
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag green">{t('projects.tag')}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
            {t('projects.h1a')}{' '}
            <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>
              {t('projects.h1b')}
            </em>
            <br />
            <span style={{ color: '#8DC21F' }}>{t('projects.h1c')}</span>
          </h2>
          <p className="text-sm text-white/50 max-w-[42ch] leading-relaxed">{t('projects.sub')}</p>
        </motion.div>

        {/* Asme-style 2-column service cards for first 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {Array.isArray(items) && items.slice(0, 2).map((item, i) => (
            <ProjectCard key={item.name} item={item} index={i} inView={inView} cardLinks={cardLinks} />
          ))}
        </div>
        {/* 4-column grid for rest */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {Array.isArray(items) && items.slice(2).map((item, i) => (
            <ProjectCard key={item.name} item={item} index={i + 2} inView={inView} cardLinks={cardLinks} />
          ))}
        </div>
      </div>
    </section>
  );
}
